<template lang="pug">
.analytics__model-info
    v-card.mb-2
        v-card-title
            | ML Model Version: {{ model.model_version }}
            v-chip.ml-2(v-if="model.is_active" color="green" small) Active
        v-card-text
            v-row
                v-col
                    v-data-table(
                        :headers="modelMetricHeaders"
                        :items="modelMetricItems"
                        :items-per-page="10"
                        dense
                        disable-pagination
                        hide-default-footer
                    )

                    div.mt-3(v-if="hasHyperParams")
                        div.d-flex.flex-wrap.align-center.mb-2
                            span.text-subtitle-2.mr-2 Hyper Params Summary:
                            v-chip.mr-2.mb-2(
                                v-for="item in summaryHyperParams"
                                :key="item.key"
                                small
                                outlined
                                color="primary"
                            )
                                span.font-weight-medium {{ item.label }}:
                                span.ml-1 {{ item.value }}

                        v-expansion-panels(accordion)
                            v-expansion-panel(
                                v-for="group in hyperParamGroups"
                                :key="group.key"
                            )
                                v-expansion-panel-header
                                    span.font-weight-medium {{ group.label }}
                                v-expansion-panel-content
                                    v-row(v-if="group.key !== 'layers'")
                                        v-col(
                                            v-for="item in group.items"
                                            :key="item.key"
                                            cols="12"
                                            sm="6"
                                            md="4"
                                        )
                                            v-card(outlined)
                                                v-card-text.py-2
                                                    div.text-caption.text-uppercase.grey--text {{ item.label }}
                                                    div.font-weight-medium.mt-1 {{ item.value }}
                                    v-row(v-else)
                                        v-col(
                                            v-for="layer in group.items"
                                            :key="layer.key"
                                            cols="12"
                                            md="6"
                                        )
                                            v-card(outlined)
                                                v-card-title.py-2
                                                    span.font-weight-medium {{ layer.label }}
                                                v-card-text.py-2
                                                    ul.pl-4.mb-0
                                                        li(
                                                            v-for="item in layer.items"
                                                            :key="item.key"
                                                            class="d-flex justify-space-between"
                                                        )
                                                            span.mr-2 {{ item.label }}
                                                            span.font-weight-medium {{ item.value }}
            v-row
                v-col
                    div(v-if="sortedForecasts.length === 0")
                        v-alert(
                            color="info"
                            text
                            dense
                            border="left"
                        ) No forecast metrics are available for this model.
                    div(v-else)
                        v-data-table(
                            :headers="forecastHeaders"
                            :items="sortedForecasts"
                            item-key="id"
                            :items-per-page="10"
                            dense
                            :footer-props="{ itemsPerPageOptions: [10, 25, 50, 100] }"
                        )
                            template(v-slot:item.start_date="{ item }")
                                span {{ formatStartDate(item.start_date, item.id) }}
                            template(v-slot:item.mean_absolute_error="{ item }")
                                span {{ formatValue(item.mean_absolute_error) }}
                            template(v-slot:item.root_mean_square_error="{ item }")
                                span {{ formatValue(item.root_mean_square_error) }}
                            template(v-slot:item.mean_absolute_percentage_error="{ item }")
                                span {{ formatValue(item.mean_absolute_percentage_error) }}%
                            template(v-slot:item.r2="{ item }")
                                span {{ formatValue(item.r2) }}
                            template(v-slot:item.mean_bias_error="{ item }")
                                span {{ formatValue(item.mean_bias_error) }}
                            template(v-slot:item.tracking_signal="{ item }")
                                span {{ formatValue(item.tracking_signal) }}

</template>

<script>
import {DateTime} from 'luxon';
export default {
    name: 'ModelInfo',
    props: {
        model: { type: Object, required: true },
        timezone: { type: String, required: true }
    },
    computed: {
        sortedForecasts () {
            if (!this.model.forecasts || !Array.isArray(this.model.forecasts)) {
                console.log('No forecasts or not an array for model:', this.model.model_version);
                return [];
            }

            return [...this.model.forecasts].sort((a, b) => {
                return new Date(b.createdAt) - new Date(a.createdAt);
            });
        },

        modelMetricHeaders () {
            return [
                { text: 'Epochs', value: 'epochs_ran' },
                { text: 'MAE', value: 'mae' },
                { text: 'MSE', value: 'mse' },
                { text: 'MAPE', value: 'mape' },
                { text: 'RMSE', value: 'rmse' }
            ];
        },

        modelMetricItems () {
            return [
                {
                    epochs_ran: this.formattedParams.epochs_ran,
                    mae: this.formattedParams.mae,
                    mse: this.formattedParams.mse,
                    mape: `${this.formattedParams.mape}%`,
                    rmse: this.formattedParams.rmse
                }
            ];
        },

        forecastHeaders () {
            return [
                { text: 'Start date', value: 'start_date' },
                { text: 'MAE', value: 'mean_absolute_error' },
                { text: 'RMSE', value: 'root_mean_square_error' },
                { text: 'MAPE', value: 'mean_absolute_percentage_error' },
                { text: 'R2', value: 'r2' },
                { text: 'MBE', value: 'mean_bias_error' },
                { text: 'Tracking Signal', value: 'tracking_signal' },
                { text: 'Model Used', value: 'model_used' }
            ];
        },

        formattedParams () {
            let params = {
                epochs_ran: 'N/A',
                mae: 'N/A',
                mse: 'N/A',
                mape: 'N/A',
                rmse: 'N/A',
                hyper_params: {}
            };

            if (!this.model.other_parameters) {
                return params;
            }

            try {
                const parsedParams = typeof this.model.other_parameters === 'string'
                    ? JSON.parse(this.model.other_parameters)
                    : this.model.other_parameters;

                if (parsedParams) {
                    // Extract specific metrics if available
                    if (parsedParams.epochs_ran) params.epochs_ran = parsedParams.epochs_ran;
                    if (parsedParams.mae) params.mae = this.formatValue(parsedParams.mae);
                    if (parsedParams.mse) params.mse = this.formatValue(parsedParams.mse);
                    if (parsedParams.mape) params.mape = this.formatValue(parsedParams.mape);
                    if (parsedParams.rmse) params.rmse = this.formatValue(parsedParams.rmse);

                    // Extract hyperparameters if available
                    if (parsedParams.hyper_params) {
                        params.hyper_params = parsedParams.hyper_params;
                    } else {
                        // If no specific hyper_params object, filter out the metrics and use rest as hyper params
                        const metricsKeys = new Set(['epochs_ran', 'mae', 'mse', 'mape', 'rmse']);
                        params.hyper_params = Object.fromEntries(
                            Object.entries(parsedParams)
                                .filter(([key]) => !metricsKeys.has(key))
                        );
                    }
                }

                return params;
            } catch (err) {
                console.error('Error parsing model parameters:', err);
                return params;
            }
        },

        browserTimezone () {
            return DateTime.local().zoneName;
        },

        hasHyperParams () {
            const hyperParams = this.formattedParams.hyper_params || {};
            return Object.keys(hyperParams).length > 0;
        },

        summaryHyperParams () {
            const hyperParams = this.formattedParams.hyper_params || {};
            const summaryKeys = [
                'lookback',
                'units',
                'layers',
                'dropout',
                'learning_rate',
                'batch_size',
                'epochs'
            ];

            return summaryKeys
                .filter((key) => hyperParams[key] !== undefined && hyperParams[key] !== null)
                .map((key) => ({
                    key,
                    label: this.formatHyperParamLabel(key),
                    value: this.formatHyperParamValue(hyperParams[key])
                }));
        },

        hyperParamGroups () {
            const hyperParams = this.formattedParams.hyper_params || {};
            const entries = Object.entries(hyperParams)
                .filter(([, value]) => value !== undefined && value !== null);

            const layerBuckets = new Map();
            const usedKeys = new Set();

            entries.forEach(([key, value]) => {
                const layerNumber = this.getLayerNumberFromKey(key);
                if (layerNumber === null) return;

                const layerKey = `layer-${layerNumber}`;
                const labelKey = this.removeLayerPrefixFromKey(key, layerNumber);
                const item = {
                    key,
                    label: this.formatHyperParamLabel(labelKey),
                    value: this.formatHyperParamValue(value)
                };

                usedKeys.add(key);
                if (layerBuckets.has(layerKey)) {
                    layerBuckets.get(layerKey).items.push(item);
                } else {
                    layerBuckets.set(layerKey, {
                        key: layerKey,
                        label: `Layer ${layerNumber}`,
                        layerNumber,
                        items: [item]
                    });
                }
            });

            const layerGroups = Array.from(layerBuckets.values())
                .sort((a, b) => a.layerNumber - b.layerNumber)
                .map((group) => ({
                    key: group.key,
                    label: group.label,
                    items: group.items
                }));

            const groups = [
                {
                    key: 'architecture',
                    label: 'Architecture',
                    keys: ['layers', 'units', 'activation', 'recurrent_activation', 'bidirectional']
                },
                {
                    key: 'training',
                    label: 'Training',
                    keys: ['epochs', 'batch_size', 'validation_split', 'shuffle', 'early_stopping']
                },
                {
                    key: 'optimization',
                    label: 'Optimization',
                    keys: ['optimizer', 'learning_rate', 'loss', 'clipnorm', 'clipvalue']
                },
                {
                    key: 'regularization',
                    label: 'Regularization',
                    keys: ['dropout', 'recurrent_dropout', 'l1', 'l2']
                },
                {
                    key: 'sequenceData',
                    label: 'Sequence/Data',
                    keys: ['lookback', 'horizon', 'feature_count', 'input_shape', 'target_column']
                }
            ];

            const grouped = groups.map((group) => {
                const items = group.keys
                    .filter((key) => !usedKeys.has(key) && hyperParams[key] !== undefined && hyperParams[key] !== null)
                    .map((key) => {
                        usedKeys.add(key);
                        return {
                            key,
                            label: this.formatHyperParamLabel(key),
                            value: this.formatHyperParamValue(hyperParams[key])
                        };
                    });

                return {
                    key: group.key,
                    label: group.label,
                    items
                };
            });

            const otherItems = entries
                .filter(([key, value]) => !usedKeys.has(key) && value !== undefined && value !== null)
                .map(([key, value]) => ({
                    key,
                    label: this.formatHyperParamLabel(key),
                    value: this.formatHyperParamValue(value)
                }));

            const groupedByKey = Object.fromEntries(grouped.map((group) => [group.key, group]));
            const allGroups = [
                groupedByKey.architecture,
                groupedByKey.training,
                groupedByKey.optimization
            ].filter(Boolean);

            if (layerGroups.length) {
                allGroups.push({
                    key: 'layers',
                    label: 'Layers',
                    items: layerGroups
                });
            }

            [groupedByKey.regularization, groupedByKey.sequenceData]
                .filter(Boolean)
                .forEach((group) => allGroups.push(group));

            if (otherItems.length) {
                allGroups.push({
                    key: 'other',
                    label: 'Other',
                    items: otherItems
                });
            }

            return allGroups.filter((group) => group.items.length);
        }
    },
    methods: {
        getLayerNumberFromKey (key) {
            const source = String(key);
            const patterns = [
                /(?:^|_)(?:layer|lstm)_?(\d+)(?:_|$)/i,
                /(?:^|_)(\d+)(?:_|$)(?:layer|lstm)(?:_|$)/i
            ];

            for (const pattern of patterns) {
                const match = source.match(pattern);
                if (!match) continue;
                const parsed = Number.parseInt(match[1], 10);
                if (Number.isFinite(parsed)) return parsed;
            }

            return null;
        },

        removeLayerPrefixFromKey (key, layerNumber) {
            const source = String(key);
            const patterns = [
                new RegExp(`(?:^|_)(?:layer|lstm)_?${layerNumber}_(.*)$`, 'i'),
                new RegExp(`(?:^|_)(?:layer|lstm)_?${layerNumber}$`, 'i')
            ];

            for (const pattern of patterns) {
                const match = source.match(pattern);
                if (!match) continue;
                if (match[1]) return match[1];
                return source;
            }

            return source;
        },

        formatHyperParamLabel (key) {
            return String(key)
                .split('_')
                .map((word) => word ? word.charAt(0).toUpperCase() + word.slice(1) : word)
                .join(' ');
        },

        formatHyperParamValue (value) {
            if (value === null || value === undefined) return 'N/A';
            if (typeof value === 'number' && Number.isFinite(value)) {
                return Number.isInteger(value) ? String(value) : Number(value.toFixed(4)).toString();
            }
            if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
                return JSON.stringify(value);
            }
            return String(value);
        },

        formatValue (val) {
            if (val === null || val === undefined || Number.isNaN(val)) return 'N/A';
            return Number(val).toFixed(3);
        },

        formatStartDate (date, forecastInfoId) {
            if (!date) return 'N/A';

            try{
                /** @type {DateTime} */
                let dt = this.formatDate(date);
                // get the hours
                if(!dt.isValid) {
                    return 'Invalid date';
                }
                if(dt.hour === 3){
                    dt = dt.plus({ hours: 1 }).setZone(this.browserTimezone);
                    return dt.isValid ? dt.toFormat('MM/dd hh:mm a ZZZZ') : 'Invalid date';
                }
                dt = dt.setZone(this.browserTimezone);
                return  dt.toFormat('MM/dd hh:mm a ZZZZ');

            } catch {
                return 'Invalid date';
            }
        },

        /**
         *
         * @param {string} date - ISO timestamp
         * @returns {DateTime} - Luxon DateTime object in UTC
         */
        formatDate (date) {
            // the date is going to have the wrong offset because it is in UTC even though Sequelize thinks it is in the server timezone
            if (!date) return 'N/A';
            let dt = DateTime.fromFormat(
                date.replace('T', ' ').replace(/Z|[+-]\d{2}:?\d{2}$/, ''),
                "yyyy-MM-dd HH:mm:ss.SSS",
                { zone: 'UTC' }
            );
            if (!dt.isValid) {
                // fallback: try parsing as ISO, then force to UTC
                dt = DateTime.fromISO(date, { zone: 'utc' });
            }
            return dt;
        }
    },
    data () {
        return {
            correctedForecastInfoId: 33
        };
    }
};
</script>
